/**
 * useScheduleMap — Fetches and mutates program_schedule_map entries for a given program.
 *
 * Uses React Query for shared cache so all consumers (ProgramViewer, useUnifiedPlanning,
 * dashboard surfaces) automatically stay in sync after move/reset operations.
 *
 * @since Step 2 — Read-only schedule map integration
 * @since Step 3 — Block-level write mutations
 * @since Step 4 — Advisory AI evaluation after moves
 * @since Step 5 — React Query shared cache for cross-surface consistency
 */

import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { ScheduleMapEntry } from "@/lib/resolveEffectiveSchedule";

export interface ScheduleAdvisory {
  risk_level: "low" | "medium" | "high";
  warnings: string[];
  suggestions: string[];
}

/** Context needed for advisory evaluation */
export interface AdvisoryContext {
  allBlocks: {
    block_index: number;
    original_day: string;
    assigned_day: string;
    focus?: string;
    session_type?: string;
    intensity_hint?: string;
    duration_hint_min?: number;
  }[];
  programTitle?: string;
}

/** Stable query key factory */
export const scheduleMapKeys = {
  all: ["schedule-map"] as const,
  byProgram: (programId: string | null) => ["schedule-map", programId] as const,
};

async function fetchScheduleMappings(
  programId: string,
  userId: string
): Promise<ScheduleMapEntry[]> {
  const { data } = await supabase
    .from("program_schedule_map")
    .select("block_index, original_day, assigned_day, assigned_time, reason, ai_warnings, source")
    .eq("program_id", programId)
    .eq("user_id", userId)
    .order("block_index", { ascending: true });

  return (data ?? []).map((row: any) => ({
    block_index: row.block_index,
    original_day: row.original_day,
    assigned_day: row.assigned_day,
    assigned_time: row.assigned_time ?? null,
    reason: row.reason ?? null,
    ai_warnings: row.ai_warnings ?? [],
    source: row.source ?? "user",
  }));
}

export function useScheduleMap(programId: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [lastAdvisory, setLastAdvisory] = useState<ScheduleAdvisory | null>(null);

  const queryKey = scheduleMapKeys.byProgram(programId);

  const { data: mappings = [], isLoading: loading } = useQuery({
    queryKey,
    queryFn: () => fetchScheduleMappings(programId!, user!.id),
    enabled: !!programId && !!user,
    staleTime: 30_000, // 30s — prevents excessive refetches while still staying fresh
  });

  /** Invalidate all schedule-map queries for this program */
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  /**
   * Request advisory evaluation from the edge function.
   * Non-blocking — errors are silently logged, never shown to the user as failures.
   */
  const evaluateAdvisory = useCallback(
    async (
      movedBlock: {
        block_index: number;
        original_day: string;
        assigned_day: string;
        focus?: string;
        session_type?: string;
        intensity_hint?: string;
        duration_hint_min?: number;
      },
      ctx: AdvisoryContext
    ): Promise<ScheduleAdvisory | null> => {
      try {
        const { data, error } = await supabase.functions.invoke("evaluate-schedule-change", {
          body: {
            moved_block: movedBlock,
            all_blocks: ctx.allBlocks,
            program_title: ctx.programTitle,
          },
        });

        if (error) {
          console.warn("Advisory evaluation failed (non-blocking):", error);
          return null;
        }

        const advisory = data as ScheduleAdvisory;
        setLastAdvisory(advisory);

        // Store warnings in the schedule map row (fire-and-forget)
        if (programId && user && advisory.warnings.length > 0) {
          supabase
            .from("program_schedule_map")
            .update({ ai_warnings: advisory.warnings })
            .eq("program_id", programId)
            .eq("user_id", user.id)
            .eq("block_index", movedBlock.block_index)
            .then(({ error: updateErr }) => {
              if (updateErr) console.warn("Failed to persist ai_warnings:", updateErr);
              else invalidate(); // Refresh cache with stored warnings
            });
        }

        return advisory;
      } catch (e) {
        console.warn("Advisory evaluation error (non-blocking):", e);
        return null;
      }
    },
    [programId, user, invalidate]
  );

  /**
   * Upsert a block-level schedule override.
   * Uses block_index as the explicit block identifier — never relies on day name alone.
   */
  const moveBlock = useCallback(
    async (params: {
      blockIndex: number;
      originalDay: string;
      assignedDay: string;
      assignedTime?: string | null;
      reason?: string | null;
    }) => {
      if (!programId || !user) return false;

      // If assigned_day === original_day, remove the override instead
      if (params.assignedDay === params.originalDay) {
        return resetBlock(params.blockIndex);
      }

      // Optimistic update
      const optimisticEntry: ScheduleMapEntry = {
        block_index: params.blockIndex,
        original_day: params.originalDay,
        assigned_day: params.assignedDay,
        assigned_time: params.assignedTime ?? null,
        reason: params.reason ?? null,
        ai_warnings: [],
        source: "user",
      };

      queryClient.setQueryData<ScheduleMapEntry[]>(queryKey, (old = []) => {
        const filtered = old.filter((m) => m.block_index !== params.blockIndex);
        return [...filtered, optimisticEntry].sort((a, b) => a.block_index - b.block_index);
      });

      const { error } = await supabase
        .from("program_schedule_map")
        .upsert(
          {
            program_id: programId,
            user_id: user.id,
            block_index: params.blockIndex,
            original_day: params.originalDay,
            assigned_day: params.assignedDay,
            assigned_time: params.assignedTime ?? null,
            reason: params.reason ?? null,
            source: "user",
          },
          { onConflict: "program_id,block_index" }
        );

      if (error) {
        console.error("moveBlock error:", error);
        toast.error("Impossible de déplacer ce bloc");
        invalidate(); // Revert optimistic on error
        return false;
      }

      toast.success("Jour modifié");
      // Confirm cache is fresh (in case of concurrent changes)
      invalidate();
      return true;
    },
    [programId, user, queryClient, queryKey, invalidate]
  );

  /**
   * Remove a block-level override so the block falls back to the original generated day.
   */
  const resetBlock = useCallback(
    async (blockIndex: number) => {
      if (!programId || !user) return false;

      // Optimistic update
      queryClient.setQueryData<ScheduleMapEntry[]>(queryKey, (old = []) =>
        old.filter((m) => m.block_index !== blockIndex)
      );

      const { error } = await supabase
        .from("program_schedule_map")
        .delete()
        .eq("program_id", programId)
        .eq("user_id", user.id)
        .eq("block_index", blockIndex);

      if (error) {
        console.error("resetBlock error:", error);
        toast.error("Impossible de réinitialiser ce bloc");
        invalidate(); // Revert optimistic on error
        return false;
      }

      setLastAdvisory(null);
      toast.success("Jour réinitialisé");
      invalidate();
      return true;
    },
    [programId, user, queryClient, queryKey, invalidate]
  );

  return {
    mappings,
    loading,
    refetch: invalidate,
    moveBlock,
    resetBlock,
    evaluateAdvisory,
    lastAdvisory,
    clearAdvisory: () => setLastAdvisory(null),
  };
}
